/**
 * The co-signed screen (SPEC §7).
 *
 * This is what makes the whole confidentiality arrangement legitimate. Without
 * it, on the day of an `urgent` alert, the child will feel betrayed — and they
 * will be right. So it is read with a parent present, both of them tap, and the
 * tutor says the same thing again in the first session.
 *
 * Four statements, in the order that matters: what the tutor sees, what the
 * parents see, when the parents are told, and why one exercise in five is
 * thrown away. The last one is not filler: it is what stops a rejected exercise
 * from being read as a judgement.
 */

import { useState } from "react";

const KEY = "tuteur.onboarding";

export function hasSignedOnboarding(personId: string | null | undefined): boolean {
  if (!personId) return false;
  return localStorage.getItem(`${KEY}.${personId}`) !== null;
}

function sign(personId: string): void {
  localStorage.setItem(`${KEY}.${personId}`, new Date().toISOString());
}

export function OnboardingScreen({
  personId,
  displayName,
  onDone,
}: {
  personId: string;
  displayName: string;
  onDone: () => void;
}) {
  const [parentPresent, setParentPresent] = useState(false);
  const [childRead, setChildRead] = useState(false);

  return (
    <main className="screen screen--onboarding">
      <h1 className="screen__title">Avant de commencer</h1>
      <p className="screen__lead">À lire à deux : {displayName} et un parent.</p>

      <section className="card">
        <h2 className="card__title">Ce que le tuteur voit</h2>
        <p>Tout ce que tu écris pendant les séances. C'est avec lui que tu travailles.</p>
      </section>

      <section className="card">
        <h2 className="card__title">Ce que tes parents voient</h2>
        <p>
          Des résumés, tes compétences et ce qui coince. <strong>Pas tes conversations.</strong>
        </p>
      </section>

      <section className="card">
        <h2 className="card__title">Quand tes parents sont prévenus</h2>
        <p>Chaque soir, un récapitulatif. Et si un jour passe sans travail, ils le savent.</p>
        <p>
          Une seule exception à la règle du dessus : si tu écris quelque chose d'inquiétant —
          danger, harcèlement, détresse — <strong>tes parents lisent ce que tu as écrit,
          immédiatement</strong>. On te le dit aujourd'hui pour que tu ne l'apprennes pas ce
          jour-là.
        </p>
      </section>

      {/* **Cet écran parlait du mauvais « un sur cinq ».** Il décrivait le taux de rejet
          de la chaîne de production — deux vérifications qui ne s'accordent pas, l'exercice
          jeté et régénéré. C'est une métrique d'usine : elle n'a aucun sens pour un enfant,
          et elle est franchement inquiétante, puisqu'elle lui annonce qu'un exercice sur
          cinq de ceux qu'on lui destinait était défectueux.

          Ce que `mastery` §3.3 demande d'annoncer est l'inverse, et c'est rassurant : la
          difficulté visée est **légèrement au-dessus** de la maîtrise estimée, donc à 80 %
          de réussite voulue, un exercice sur cinq échoue **par construction**. C'est le
          réglage, pas un verdict — « sans cette explication, Baya lira chaque échec comme
          une information sur elle-même ».

          Les deux taux valent un cinquième par coïncidence, et c'est probablement ce qui a
          fait écrire l'un pour l'autre. */}
      <section className="card">
        <h2 className="card__title">Pourquoi tu n'auras pas tout bon</h2>
        <p>
          Les exercices sont choisis <strong>un peu au-dessus</strong> de ce que tu sais déjà
          faire. C'est voulu : sur cinq exercices, il est normal d'en rater un.
        </p>
        <p>
          Si tu réussissais tout, ça voudrait dire qu'ils sont trop faciles et que tu
          n'apprends rien. <strong>Un exercice raté n'est pas une note</strong> — c'est ce qui
          montre où travailler ensuite.
        </p>
      </section>

      <section className="card card--sign">
        <label className="check">
          <input
            type="checkbox"
            checked={parentPresent}
            onChange={(event) => setParentPresent(event.target.checked)}
          />
          <span>Un parent est là et a lu cet écran.</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={childRead}
            onChange={(event) => setChildRead(event.target.checked)}
          />
          <span>J'ai lu et j'ai compris.</span>
        </label>

        <button
          type="button"
          className="btn btn--primary"
          disabled={!parentPresent || !childRead}
          onClick={() => {
            sign(personId);
            onDone();
          }}
        >
          On commence
        </button>
      </section>
    </main>
  );
}
